// Flashcard drill. Random order in v1; SRS deferred. Press Space to advance the
// reveal stage (hz → +pinyin → +translation), J/F to mark "I know it",
// K/D to mark "still learning". Auto-exits after the last card.
//
// Each card emits a flashcard_attempt event with timing + correctness +
// "unaided" (whether the user revealed before answering). The admin dashboard
// builds the mastery and forgetting curves from these rows.

import { useEffect, useMemo, useRef, useState } from 'react';
import { X, RotateCcw, Check } from 'lucide-react';
import type { LibraryWord } from '@/lib/types';
import { Events, track } from '@/lib/observability';
import { SealStamp } from '@/components/visual/SealStamp';
import { InkMountains } from '@/components/visual/InkMountains';
import { C } from '@/styles/tokens';

type RevealStage = 'hidden' | 'pinyin' | 'full';

interface FlashcardModeProps {
  library: LibraryWord[];
  onClose: () => void;
  onMarkKnown: (hz: string) => void;
}

function shuffle<T>(items: T[]): T[] {
  const arr = items.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
}

export function FlashcardMode({ library, onClose, onMarkKnown }: FlashcardModeProps) {
  const deck = useMemo(() => shuffle(library), [library]);
  const [idx, setIdx] = useState(0);
  const [stage, setStage] = useState<RevealStage>('hidden');
  // Time the card was first shown (or last reset). Latency = answer ts minus
  // this. Reset on each card so the timer measures *this* attempt only.
  const cardShownAt = useRef<number>(Date.now());
  const card = deck[idx];
  const showPinyin = stage !== 'hidden';
  const showEn = stage === 'full';

  useEffect(() => {
    cardShownAt.current = Date.now();
  }, [idx]);

  function recordAttempt(correct: boolean) {
    if (!card) return;
    track(Events.FlashcardAttempt, {
      hz: card.hz,
      correct,
      latencyMs: Date.now() - cardShownAt.current,
      // The user is "unaided" if they answered with hz only (no pinyin/translation revealed).
      unaided: stage === 'hidden',
      // Best-effort exposure number: seenCount before this attempt + 1.
      // Server's per-(user, hz) ordering is the source of truth for learning
      // curves, but this is a cheap hint so the row lands well-formed.
      exposureN: (card.seenCount ?? 0) + 1,
    });
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === ' ') {
        e.preventDefault();
        setStage((s) => (s === 'hidden' ? 'pinyin' : s === 'pinyin' ? 'full' : 'hidden'));
      } else if (e.key === 'j' || e.key === 'J' || e.key === 'f' || e.key === 'F') {
        recordAttempt(true);
        if (card) onMarkKnown(card.hz);
        nextCard();
      } else if (e.key === 'k' || e.key === 'K' || e.key === 'd' || e.key === 'D') {
        recordAttempt(false);
        nextCard();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card, stage]);

  function nextCard() {
    if (idx + 1 >= deck.length) {
      onClose();
      return;
    }
    setStage('hidden');
    setIdx((i) => i + 1);
  }

  function flipStage() {
    setStage((s) => (s === 'hidden' ? 'pinyin' : s === 'pinyin' ? 'full' : 'hidden'));
  }

  if (!card) {
    return (
      <div role="dialog" aria-modal aria-label="Flashcards" className="fixed inset-0 z-40 flex items-center justify-center" style={{ background: C.cream }}>
        <div className="text-center">
          <p className="ff-display text-3xl mb-1" style={{ color: C.ink }}>No words yet</p>
          <p className="ff-zh-serif text-base mb-2" style={{ color: C.inkLight }}>
            还没有生词
          </p>
          <p className="ff-body text-sm mb-6" style={{ color: C.inkLight }}>
            Save words from chats and stories to start drilling.
          </p>
          <button type="button" onClick={onClose} className="px-5 py-2 rounded-full text-white inline-flex items-center gap-1.5" style={{ background: C.vermillion }}>
            Close
            <span className="ff-zh-serif opacity-80">关闭</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div role="dialog" aria-modal aria-label="Flashcards" className="fixed inset-0 z-40 flex flex-col overflow-hidden" style={{ background: C.paper }}>
      {/* Header: X on the left, centered 练习 + counter, balance spacer on the right. */}
      <header className="grid grid-cols-[40px_1fr_40px] items-start px-4 py-3 safe-top relative">
        <button
          type="button"
          onClick={onClose}
          className="w-9 h-9 rounded-full flex items-center justify-center"
          aria-label="Close flashcards"
          style={{ background: 'white', boxShadow: '0 1px 4px rgba(26,22,20,0.06)' }}
        >
          <X size={18} aria-hidden />
        </button>
        <div className="text-center -mt-1">
          <div className="ff-zh-serif font-bold text-3xl leading-none" style={{ color: C.ink }}>
            练习
          </div>
          <div className="ff-body text-[12px] mt-1" style={{ color: C.inkLight }}>
            {idx + 1} / {deck.length}
          </div>
        </div>
        <span aria-hidden />
      </header>

      <div className="flex-1 flex flex-col items-center justify-center px-5 safe-bottom">
        <button
          type="button"
          onClick={flipStage}
          className="relative w-full max-w-[380px] aspect-square rounded-3xl flex flex-col items-center justify-center text-center px-6 anim-scale-in overflow-hidden shadow-paper-lg"
          style={{ background: C.cream, border: `1px solid ${C.mist}` }}
          aria-label={
            stage === 'hidden'
              ? 'Reveal pinyin'
              : stage === 'pinyin'
                ? 'Show translation'
                : 'Hide pinyin and translation'
          }
        >
          <SealStamp char="字" size={32} className="absolute top-3 right-3" />

          {/* Big Hanzi. */}
          <span
            className="ff-zh-serif font-bold leading-none"
            style={{ color: C.ink, fontSize: 'clamp(72px, 22vw, 110px)' }}
          >
            {card.hz}
          </span>

          {showPinyin && (
            <div className="mt-5 anim-slide-up">
              <div className="ff-body text-base opacity-80" style={{ color: C.ink }}>
                {card.py}
              </div>
              {showEn && (
                <div className="ff-body text-base italic mt-0.5" style={{ color: C.inkLight }}>
                  {card.en}
                </div>
              )}
            </div>
          )}
          {stage === 'hidden' && (
            <span
              className="mt-6 ff-body text-[11px] uppercase tracking-[0.25em] inline-flex items-center gap-2"
              style={{ color: C.inkSofter }}
            >
              <span aria-hidden style={{ color: C.vermillion, fontSize: 10 }}>◇</span>
              Tap for pinyin
            </span>
          )}
          {stage === 'pinyin' && (
            <span
              className="mt-4 ff-body text-[11px] uppercase tracking-[0.25em] inline-flex items-center gap-2"
              style={{ color: C.inkSofter }}
            >
              <span aria-hidden style={{ color: C.vermillion, fontSize: 10 }}>◇</span>
              Tap for translation
            </span>
          )}

          <InkMountains
            height={64}
            className="absolute left-0 right-0 bottom-0"
            tint={C.ink}
          />
        </button>

        <p
          className="ff-body text-[11px] mt-3 text-center italic"
          style={{ color: C.inkSofter }}
          aria-live="polite"
        >
          Seen {card.seenCount} {card.seenCount === 1 ? 'time' : 'times'} in chat
        </p>

        {/* Buttons live right under the card, sharing its max-width so the
            whole stack (card + caption + buttons) reads as one centered
            group. Hanzi sits as a small subtitle beneath the English label so
            it can't overflow the pill. */}
        <div className="w-full max-w-[380px] mt-4 flex gap-3">
          <button
            type="button"
            onClick={() => {
              recordAttempt(false);
              nextCard();
            }}
            className="flex-1 py-2.5 rounded-2xl flex flex-col items-center justify-center gap-0.5"
            style={{
              background: 'white',
              color: C.ink,
              border: `1px solid ${C.blush}`,
              boxShadow: '0 1px 3px rgba(26,22,20,0.04)',
            }}
          >
            <span className="ff-body font-semibold text-[14px] inline-flex items-center gap-1.5 leading-none">
              <RotateCcw size={13} aria-hidden style={{ color: C.gold }} />
              Still learning
            </span>
            <span className="ff-zh-serif text-[11px] leading-none" style={{ color: C.inkLight }}>
              还在学
            </span>
          </button>
          <button
            type="button"
            onClick={() => {
              recordAttempt(true);
              onMarkKnown(card.hz);
              nextCard();
            }}
            className="flex-1 py-2.5 rounded-2xl flex flex-col items-center justify-center gap-0.5 text-white"
            style={{ background: C.jade, boxShadow: '0 4px 12px rgba(92,138,107,0.30)' }}
          >
            <span className="ff-body font-semibold text-[14px] inline-flex items-center gap-1.5 leading-none">
              <Check size={13} aria-hidden />
              I know this
            </span>
            <span className="ff-zh-serif text-[11px] leading-none opacity-90">
              我会了
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
